'use client';

// Scène d'une animation : un calque SVG (formes) et un calque HTML superposé
// (textes, formules, nombres, étiquettes — pas de <foreignObject>), dans le
// repère du canevas (360 × 640 par défaut), mis à l'échelle de la place
// disponible. React monte les objets UNE fois ; la boucle d'affichage écrit
// ensuite l'état de chaque objet par références (`apply`), sans re-rendu.
//
// Éclairage (objet nommé par la voix, `object.highlight`) : halo doré par un
// filtre SVG (4 intensités, compatibles iOS) sur les formes, trait un peu
// épaissi ; `drop-shadow` CSS sur les textes, formules, nombres et étiquettes.
//
// Un texte, une formule ou un nombre plus large que la scène est RÉDUIT à
// l'affichage autour de son ancrage (mesuré avec les vraies polices, à
// chaque changement de contenu et quand les polices arrivent) : jamais coupé.

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  resolveColor,
  roomAt,
  type AnimationState,
  type CompiledAnimation,
  type ObjectState,
  type ResolvedObject,
} from '@jeraisonne/animation';
import { cn } from '../../lib/utils';
import { escapeHtml, formulaToHtml, mathTextToHtml } from './animation-math';
import { GLOW_LEVELS, GLOW_STROKE_GAIN, glowLevel, glowRegion } from './glow';

export interface StageHandle {
  /** écrit l'état de chaque objet dans le DOM */
  apply(state: AnimationState): void;
}

export interface AnimationStageProps {
  anim: CompiledAnimation;
  /** URL d'une ressource du bucket (images) ; absente : images remplacées par leur texte */
  assetUrl?: (path: string) => string;
  /** calque au-dessus des objets, dans le repère du canevas (sous-titres, questions…) */
  overlay?: ReactNode;
  className?: string;
}

const HTML_TYPES = new Set(['text', 'formula', 'value']);

interface Handle {
  obj: ResolvedObject;
  svgWrap: SVGGElement | null;
  shape: SVGGeometryElement | null;
  heads: SVGPolygonElement[];
  htmlWrap: HTMLDivElement | null;
  el: HTMLElement | null;
  prev: HTMLElement | null;
  cur: HTMLElement | null;
  gesture: HTMLElement | null;
  /** cadre `box` d'un texte (bordure animée par `object.style`) */
  box: HTMLElement | null;
  /** bloc ancré d'un texte, d'une formule, d'un nombre : réduit s'il ne tient pas */
  anchor: HTMLElement | null;
  /** largeur dont il dispose dans la scène (null : pas de réduction) */
  room: number | null;
  length: number | null;
  /** préfixe des filtres d'éclairage de la scène (`<id>-1` … `<id>-4`) */
  glowId: string;
  last: Record<string, string | number | null | undefined>;
}

function newHandle(obj: ResolvedObject, glowId: string): Handle {
  return {
    obj,
    glowId,
    svgWrap: null,
    shape: null,
    heads: [],
    htmlWrap: null,
    el: null,
    prev: null,
    cur: null,
    gesture: null,
    box: null,
    anchor: null,
    room: null,
    length: null,
    last: {},
  };
}

// ------------------------------------------------------------- transforms

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function isIdentity(s: ObjectState): boolean {
  return s.dx === 0 && s.dy === 0 && s.rotate === 0 && s.scale * s.pop === 1;
}

export function svgTransform(s: ObjectState): string {
  if (isIdentity(s)) return '';
  const k = round(s.scale * s.pop);
  const { x: rx, y: ry } = s.rotateOrigin;
  const { x: sx, y: sy } = s.scaleOrigin;
  return (
    `translate(${round(s.dx)} ${round(s.dy)}) rotate(${round(s.rotate)} ${rx} ${ry}) ` +
    `translate(${sx} ${sy}) scale(${k}) translate(${-sx} ${-sy})`
  );
}

export function cssTransform(s: ObjectState): string {
  if (isIdentity(s)) return '';
  const k = round(s.scale * s.pop);
  const { x: rx, y: ry } = s.rotateOrigin;
  const { x: sx, y: sy } = s.scaleOrigin;
  return (
    `translate(${round(s.dx)}px, ${round(s.dy)}px) translate(${rx}px, ${ry}px) ` +
    `rotate(${round(s.rotate)}deg) translate(${-rx}px, ${-ry}px) ` +
    `translate(${sx}px, ${sy}px) scale(${k}) translate(${-sx}px, ${-sy}px)`
  );
}

const numberFormats = new Map<number, Intl.NumberFormat>();
export function formatValue(v: number, decimals: number): string {
  let f = numberFormats.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    numberFormats.set(decimals, f);
  }
  return f.format(v);
}

// ---------------------------------------------------------------- apply

function set(h: Handle, key: string, value: string | number | null | undefined): boolean {
  if (h.last[key] === value) return false;
  h.last[key] = value;
  return true;
}

const FIT_ORIGIN = { start: '0 50%', middle: '50% 50%', end: '100% 50%' } as const;

/**
 * Largeur VUE du bloc ancré (repère du canevas, sans les transformations) :
 * une formule a une marge intérieure de 8 px (transparente) ; un cadre `box`
 * déborde autour du texte (marges négatives).
 */
function visibleWidth(h: Handle, a: HTMLElement): number {
  const o = h.obj;
  const w = a.offsetWidth;
  if (o.type === 'formula') {
    const frame = o.box ? 2 * (o.box.padding + o.box.strokeWidth) : 0;
    return Math.max(0, w - 16) + frame;
  }
  if ((o.type === 'text' || o.type === 'value') && o.box)
    return w + 2 * (o.box.padding + o.box.strokeWidth);
  return w;
}

/** Réduit le bloc s'il est plus large que la place dont il dispose. */
function fitHandle(h: Handle): void {
  const a = h.anchor;
  const o = h.obj;
  if (!a || h.room === null || !('align' in o)) return;
  const w = visibleWidth(h, a);
  const f = w > 0 && w > h.room ? round(Math.max(0.3, h.room / w)) : 1;
  if (!set(h, 'fit', f)) return;
  a.style.transform = f < 1 ? `${anchorTranslate(o.align)} scale(${f})` : anchorTranslate(o.align);
  a.style.transformOrigin = f < 1 ? FIT_ORIGIN[o.align] : '';
  if (f < 1) a.dataset.fit = String(f);
  else delete a.dataset.fit;
}

function applyHandle(h: Handle, s: ObjectState): void {
  const o = h.obj;
  const opacity = round(Math.max(0, Math.min(1, s.opacity * s.styleOpacity)));
  const hidden = s.opacity <= 0;
  for (const wrap of [h.svgWrap, h.htmlWrap]) {
    if (!wrap) continue;
    const key = wrap === h.svgWrap ? 'svg' : 'html';
    if (set(h, `${key}:opacity`, opacity)) wrap.style.opacity = String(opacity);
    if (set(h, `${key}:hidden`, hidden ? 1 : 0)) wrap.style.visibility = hidden ? 'hidden' : '';
  }
  // éclairage : halo doré (SVG par paliers, HTML en continu)
  const glow = round(Math.max(0, Math.min(1, s.glow)));
  const level = glowLevel(glow);
  if (set(h, 'glow', glow)) {
    if (h.svgWrap && set(h, 'glow:level', level)) {
      if (level) h.svgWrap.setAttribute('filter', `url(#${h.glowId}-${level})`);
      else h.svgWrap.removeAttribute('filter');
    }
    if (h.el)
      h.el.style.filter =
        glow > 0.02
          ? `drop-shadow(0 0 ${round(2 + 6 * glow)}px rgba(242, 193, 78, ${round(0.95 * glow)}))`
          : '';
  }

  if (h.svgWrap) {
    const t = svgTransform(s);
    if (set(h, 'svg:t', t)) {
      if (t) h.svgWrap.setAttribute('transform', t);
      else h.svgWrap.removeAttribute('transform');
    }
  }
  if (h.htmlWrap) {
    const t = cssTransform(s);
    if (set(h, 'html:t', t)) h.htmlWrap.style.transform = t;
  }

  // formes : couleurs, épaisseur, tracé
  if (h.shape) {
    if (s.fill !== null && set(h, 'fill', s.fill)) h.shape.setAttribute('fill', s.fill);
    if (s.stroke !== null && set(h, 'stroke', s.stroke)) h.shape.setAttribute('stroke', s.stroke);
    if (s.strokeWidth !== null) {
      const gain = level ? GLOW_STROKE_GAIN * GLOW_LEVELS[level - 1]! : 0;
      const sw = round(s.strokeWidth * (1 + gain));
      if (set(h, 'sw', sw)) h.shape.setAttribute('stroke-width', String(sw));
    }
    const draw = round(s.draw);
    if (set(h, 'draw', draw)) {
      if (draw < 1) {
        if (h.length === null) {
          try {
            h.length = h.shape.getTotalLength?.() ?? 0;
          } catch {
            h.length = 0;
          }
        }
        const L = h.length || 1000;
        h.shape.style.strokeDasharray = `${L} ${L}`;
        h.shape.style.strokeDashoffset = String(round(L * (1 - draw)));
      } else {
        h.shape.style.strokeDasharray = 'style' in o && o.style?.dash ? dashFor(s) : '';
        h.shape.style.strokeDashoffset = '';
      }
    }
    for (const head of h.heads) {
      if (s.stroke !== null && set(h, 'headFill', s.stroke)) {
        for (const x of h.heads) x.setAttribute('fill', s.stroke);
      }
      head.style.opacity = draw >= 0.95 ? '' : '0';
    }
  }

  if (h.box) {
    if (s.stroke !== null && set(h, 'boxStroke', s.stroke)) h.box.style.borderColor = s.stroke;
    if (s.strokeWidth !== null && set(h, 'boxWidth', s.strokeWidth) && 'box' in o && o.box) {
      h.box.style.borderWidth = `${s.strokeWidth}px`;
      h.box.style.margin = boxMargin(o.type, o.box.padding, s.strokeWidth);
    }
  }

  switch (o.type) {
    case 'text':
    case 'point': {
      if (!h.el) break;
      if (o.type === 'text' && s.fill && set(h, 'color', s.fill)) h.el.style.color = s.fill;
      if (set(h, 'label', s.label)) {
        h.el.innerHTML = s.label ? mathTextToHtml(s.label) : '';
        h.el.style.display = s.label ? '' : 'none';
        fitHandle(h);
      }
      const fade = s.labelProgress < 1 ? round(s.labelProgress) : null;
      if (set(h, 'labelFade', fade)) h.el.style.opacity = fade === null ? '' : String(fade);
      break;
    }
    case 'formula': {
      if (!h.cur || !h.prev || !h.el) break;
      if (s.fill && set(h, 'color', s.fill)) h.el.style.color = s.fill;
      let changed = false;
      if (set(h, 'tex', s.tex)) {
        h.cur.innerHTML = s.tex ? formulaToHtml(s.tex) : '';
        changed = true;
      }
      if (set(h, 'texFrom', s.texFrom)) {
        h.prev.innerHTML = s.texFrom ? formulaToHtml(s.texFrom) : '';
        h.prev.style.display = s.texFrom ? '' : 'none';
        changed = true;
      }
      if (changed) fitHandle(h);
      const p = s.texFrom ? round(s.texProgress) : 1;
      if (set(h, 'texP', p)) {
        h.cur.style.opacity = p < 1 ? String(p) : '';
        h.cur.style.transform = p < 1 ? `translateY(${round((1 - p) * 10)}px)` : '';
        h.prev.style.opacity = String(round(1 - p));
        h.prev.style.transform = `translateY(${round(-p * 10)}px)`;
      }
      const flash = round(s.flash);
      if (set(h, 'flash', flash)) {
        const fill = o.box?.fill && o.box.fill !== 'none' ? resolveColor(o.box.fill) : '';
        h.el.style.backgroundColor =
          flash > 0 ? `rgba(244, 198, 78, ${round(flash * 0.22)})` : fill;
        h.el.style.boxShadow =
          flash > 0 ? `0 0 ${round(24 * flash)}px rgba(244, 198, 78, ${round(flash * 0.45)})` : '';
      }
      if (h.gesture && set(h, 'gesture', s.gesture)) {
        if (s.gesture) h.gesture.innerHTML = mathTextToHtml(s.gesture);
        h.gesture.style.opacity = s.gesture ? '1' : '0';
      }
      break;
    }
    case 'value': {
      if (!h.el || s.value === null) break;
      if (s.fill && set(h, 'color', s.fill)) h.el.style.color = s.fill;
      const text = formatValue(s.value, o.decimals);
      if (set(h, 'value', text)) {
        h.el.innerHTML =
          (o.prefix ? mathTextToHtml(o.prefix) : '') +
          escapeHtml(text) +
          (o.suffix ? mathTextToHtml(o.suffix) : '');
        fitHandle(h);
      }
      break;
    }
    default:
      break;
  }
}

type TextBox = NonNullable<Extract<ResolvedObject, { type: 'text' }>['box']>;

/**
 * Le cadre déborde AUTOUR du texte (marges négatives) : l'ancrage `at` et
 * la place du texte ne changent pas, que le texte soit encadré ou non. Une
 * formule a déjà sa marge intérieure (`px-2 py-1`), déduite ici.
 */
function boxMargin(type: string, padding: number, width: number): string {
  if (type === 'formula') return `${-(padding - 4 + width)}px ${-(padding - 8 + width)}px`;
  return `${-(padding + width)}px`;
}

function boxStyle(type: string, box: TextBox, stroke: string): React.CSSProperties {
  return {
    padding: box.padding,
    margin: boxMargin(type, box.padding, box.strokeWidth),
    border: `${box.strokeWidth}px solid ${stroke}`,
    borderRadius: box.radius,
    backgroundColor: box.fill && box.fill !== 'none' ? resolveColor(box.fill) : undefined,
  };
}

function dashFor(s: ObjectState): string {
  const w = s.strokeWidth ?? 2;
  return `${round(w * 3)} ${round(w * 2.4)}`;
}

// --------------------------------------------------------------- rendu

function anchorTranslate(align: 'start' | 'middle' | 'end'): string {
  return align === 'start'
    ? 'translate(0, -50%)'
    : align === 'end'
      ? 'translate(-100%, -50%)'
      : 'translate(-50%, -50%)';
}

const LABEL_OFFSETS: Record<string, { x: number; y: number; t: string }> = {
  n: { x: 0, y: -12, t: 'translate(-50%, -100%)' },
  ne: { x: 8, y: -8, t: 'translate(0, -100%)' },
  e: { x: 11, y: 0, t: 'translate(0, -50%)' },
  se: { x: 8, y: 8, t: 'translate(0, 0)' },
  s: { x: 0, y: 12, t: 'translate(-50%, 0)' },
  sw: { x: -8, y: 8, t: 'translate(-100%, 0)' },
  w: { x: -11, y: 0, t: 'translate(-100%, -50%)' },
  nw: { x: -8, y: -8, t: 'translate(-100%, -100%)' },
};

function arrowHeads(o: Extract<ResolvedObject, { type: 'arrow' }>) {
  const w = o.style?.strokeWidth ?? 3;
  const len = Math.max(10, 3.2 * w + 4);
  const half = len * 0.55;
  const head = (tipX: number, tipY: number, fromX: number, fromY: number) => {
    const a = Math.atan2(tipY - fromY, tipX - fromX);
    const bx = tipX - len * Math.cos(a);
    const by = tipY - len * Math.sin(a);
    const px = -Math.sin(a) * half;
    const py = Math.cos(a) * half;
    return {
      points: `${round(tipX)},${round(tipY)} ${round(bx + px)},${round(by + py)} ${round(bx - px)},${round(by - py)}`,
      base: { x: tipX - len * 0.8 * Math.cos(a), y: tipY - len * 0.8 * Math.sin(a) },
    };
  };
  const end = head(o.x2, o.y2, o.x1, o.y1);
  const start = o.head === 'both' ? head(o.x1, o.y1, o.x2, o.y2) : null;
  return {
    heads: start ? [end.points, start.points] : [end.points],
    line: {
      x1: start ? start.base.x : o.x1,
      y1: start ? start.base.y : o.y1,
      x2: end.base.x,
      y2: end.base.y,
    },
  };
}

/** Place de chaque texte, formule, nombre dans la scène (ancrage + décalage de ses groupes). */
function textRooms(anim: CompiledAnimation): Map<string, number> {
  const W = anim.doc.canvas.width;
  const byId = new Map(anim.objects.map((o) => [o.id, o]));
  const out = new Map<string, number>();
  for (const o of anim.objects) {
    if (o.type !== 'text' && o.type !== 'formula' && o.type !== 'value') continue;
    if (o.type === 'text' && o.maxWidth !== undefined) continue; // coupé en lignes
    let x = o.at.x;
    let p = o.parent !== undefined ? byId.get(o.parent) : undefined;
    for (let guard = 0; p && guard < 16; guard++) {
      if (p.type === 'group') x += p.x;
      p = p.parent !== undefined ? byId.get(p.parent) : undefined;
    }
    const room = roomAt(x, o.align, W);
    // collé au bord : il faut le déplacer, pas l'écraser
    if (room >= 40) out.set(o.id, room);
  }
  return out;
}

export const AnimationStage = forwardRef<StageHandle, AnimationStageProps>(function AnimationStage(
  { anim, assetUrl, overlay, className },
  ref,
) {
  const { width: W, height: H } = anim.doc.canvas;
  // identifiant des filtres propre à cette scène (plusieurs lecteurs par page)
  const glowId = `jr-glow-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const handles = useMemo(() => {
    const m = new Map<string, Handle>();
    const rooms = textRooms(anim);
    for (const o of anim.objects) {
      const hd = newHandle(o, glowId);
      hd.room = rooms.get(o.id) ?? null;
      m.set(o.id, hd);
    }
    return m;
  }, [anim, glowId]);
  const region = useMemo(() => glowRegion(anim.objects, W, H), [anim, W, H]);
  const children = useMemo(() => {
    const m = new Map<string | null, ResolvedObject[]>();
    for (const o of anim.objects) {
      const key = o.parent ?? null;
      m.set(key, [...(m.get(key) ?? []), o]);
    }
    return m;
  }, [anim]);
  const assets = useMemo(() => new Map(anim.doc.assets.map((a) => [a.id, a])), [anim]);

  useImperativeHandle(
    ref,
    () => ({
      apply(state: AnimationState) {
        for (const [id, h] of handles) {
          const s = state[id];
          if (s) applyHandle(h, s);
        }
      },
    }),
    [handles],
  );

  // état initial posé avant le premier affichage (pas de flash)
  useLayoutEffect(() => {
    for (const [id, h] of handles) {
      const s = anim.initial[id];
      if (s) applyHandle(h, s);
    }
  }, [anim, handles]);

  // mise à l'échelle du calque HTML
  const rootRef = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(1);
  // avant le premier affichage : pas d'image à l'échelle 1 qui saute
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const update = () => setK(el.clientWidth > 0 ? el.clientWidth / W : 1);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [W]);

  const h = (id: string) => handles.get(id)!;

  // les polices arrivent après le premier affichage : on remesure
  useEffect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts) return;
    let live = true;
    const refit = () => {
      if (live) for (const hd of handles.values()) fitHandle(hd);
    };
    void fonts.ready?.then(refit);
    fonts.addEventListener?.('loadingdone', refit);
    return () => {
      live = false;
      fonts.removeEventListener?.('loadingdone', refit);
    };
  }, [handles]);

  const renderSvg = (o: ResolvedObject): ReactNode => {
    if (HTML_TYPES.has(o.type)) return null;
    const hd = h(o.id);
    const wrap = (node: ReactNode) => (
      <g
        key={o.id}
        data-object={o.id}
        ref={(el) => {
          hd.svgWrap = el;
        }}
      >
        {node}
      </g>
    );
    const shapeRef = (el: SVGGeometryElement | null) => {
      hd.shape = el;
    };
    const common = {
      ref: shapeRef,
      strokeLinecap: 'round' as const,
      strokeLinejoin: 'round' as const,
    };
    switch (o.type) {
      case 'group':
        return wrap(
          <g transform={`translate(${o.x} ${o.y})`}>{(children.get(o.id) ?? []).map(renderSvg)}</g>,
        );
      case 'rect':
        return wrap(
          <rect {...common} x={o.x} y={o.y} width={o.width} height={o.height} rx={o.rx} />,
        );
      case 'circle':
        return wrap(<circle {...common} cx={o.cx} cy={o.cy} r={o.r} />);
      case 'ellipse':
        return wrap(<ellipse {...common} cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} />);
      case 'line':
        return wrap(<line {...common} x1={o.x1} y1={o.y1} x2={o.x2} y2={o.y2} />);
      case 'arrow': {
        const g = arrowHeads(o);
        return wrap(
          <>
            <line {...common} {...g.line} />
            {g.heads.map((pts, i) => (
              <polygon
                key={i}
                points={pts}
                ref={(el) => {
                  if (el && !hd.heads.includes(el)) hd.heads.push(el);
                }}
              />
            ))}
          </>,
        );
      }
      case 'polygon':
        return wrap(
          <polygon {...common} points={o.points.map((p) => `${p.x},${p.y}`).join(' ')} />,
        );
      case 'path':
        return wrap(<path {...common} d={o.d} />);
      case 'point':
        return wrap(<circle {...common} cx={o.x} cy={o.y} r={5} />);
      case 'image': {
        const a = assets.get(o.asset);
        const url = a && assetUrl ? assetUrl(a.path) : null;
        return wrap(
          url ? (
            <image
              href={url}
              x={o.x}
              y={o.y}
              width={o.width}
              height={o.height}
              preserveAspectRatio="xMidYMid meet"
            />
          ) : (
            <rect
              x={o.x}
              y={o.y}
              width={o.width}
              height={o.height}
              fill="none"
              stroke={resolveColor('muted')}
              strokeDasharray="6 5"
            />
          ),
        );
      }
      default:
        return null;
    }
  };

  const renderHtml = (o: ResolvedObject): ReactNode => {
    const hd = h(o.id);
    const wrap = (node: ReactNode) => (
      <div
        key={o.id}
        data-object={o.id}
        className="absolute left-0 top-0"
        style={{ transformOrigin: '0 0' }}
        ref={(el) => {
          hd.htmlWrap = el;
        }}
      >
        {node}
      </div>
    );
    switch (o.type) {
      case 'group': {
        const inner = (children.get(o.id) ?? []).map(renderHtml).filter(Boolean);
        if (inner.length === 0) return null;
        return wrap(
          <div
            className="absolute left-0 top-0"
            style={{ transform: `translate(${o.x}px, ${o.y}px)` }}
          >
            {inner}
          </div>,
        );
      }
      case 'text': {
        const style: React.CSSProperties = {
          left: o.at.x,
          top: o.at.y,
          transform: anchorTranslate(o.align),
          fontSize: o.size,
          fontWeight: o.weight === 'bold' ? 700 : 400,
          whiteSpace: o.maxWidth ? 'normal' : 'nowrap',
          width: o.maxWidth,
          textAlign: o.align === 'start' ? 'left' : o.align === 'end' ? 'right' : 'center',
        };
        if (!o.box)
          return wrap(
            <div
              ref={(el) => {
                hd.el = el;
                hd.anchor = el;
              }}
              className="absolute leading-tight"
              style={style}
            />,
          );
        // encadré : le cadre suit le texte rendu (bloc en ligne qui épouse le texte)
        return wrap(
          <div
            ref={(el) => {
              hd.anchor = el;
            }}
            className="absolute leading-tight"
            style={style}
          >
            <span
              ref={(el) => {
                hd.el = el;
                hd.box = el;
              }}
              data-box=""
              className="inline-block align-top"
              style={boxStyle('text', o.box, resolveColor(o.box.stroke ?? o.color ?? 'paper'))}
            />
          </div>,
        );
      }
      case 'formula':
        return wrap(
          <div
            ref={(el) => {
              hd.anchor = el;
            }}
            className="absolute"
            style={{ left: o.at.x, top: o.at.y, transform: anchorTranslate(o.align) }}
          >
            <span
              ref={(el) => {
                hd.gesture = el;
              }}
              className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[13px] font-semibold"
              style={{
                opacity: 0,
                color: resolveColor('night'),
                backgroundColor: resolveColor('gold'),
                transition: 'opacity 150ms',
              }}
            />
            <span
              ref={(el) => {
                hd.el = el;
                if (o.box) hd.box = el;
              }}
              data-box={o.box ? '' : undefined}
              className="grid whitespace-nowrap rounded-lg px-2 py-1"
              style={{
                fontSize: o.size,
                ...(o.box
                  ? boxStyle('formula', o.box, resolveColor(o.box.stroke ?? o.color ?? 'paper'))
                  : {}),
              }}
            >
              <span
                ref={(el) => {
                  hd.prev = el;
                }}
                className="[grid-area:1/1]"
                style={{ display: 'none' }}
              />
              <span
                ref={(el) => {
                  hd.cur = el;
                }}
                className="[grid-area:1/1]"
              />
            </span>
          </div>,
        );
      case 'value': {
        const style: React.CSSProperties = {
          left: o.at.x,
          top: o.at.y,
          transform: anchorTranslate(o.align),
          fontSize: o.size,
        };
        const cls = 'absolute whitespace-nowrap font-semibold tabular-nums leading-tight';
        if (!o.box)
          return wrap(
            <div
              ref={(el) => {
                hd.el = el;
                hd.anchor = el;
              }}
              className={cls}
              style={style}
            />,
          );
        return wrap(
          <div
            ref={(el) => {
              hd.anchor = el;
            }}
            className={cls}
            style={style}
          >
            <span
              ref={(el) => {
                hd.el = el;
                hd.box = el;
              }}
              data-box=""
              className="inline-block align-top"
              style={boxStyle('value', o.box, resolveColor(o.box.stroke ?? o.color ?? 'paper'))}
            />
          </div>,
        );
      }
      case 'point': {
        const off = LABEL_OFFSETS[o.labelPosition] ?? LABEL_OFFSETS.ne!;
        return wrap(
          <div
            ref={(el) => {
              hd.el = el;
            }}
            className="absolute whitespace-nowrap text-[17px] font-semibold italic leading-none"
            style={{
              left: o.x + off.x,
              top: o.y + off.y,
              transform: off.t,
              color: o.style?.fill ? resolveColor(o.style.fill) : resolveColor('paper'),
            }}
          />,
        );
      }
      default:
        return null;
    }
  };

  const roots = children.get(null) ?? [];
  return (
    <div
      ref={rootRef}
      className={cn('relative w-full select-none overflow-hidden', className)}
      style={{
        aspectRatio: `${W} / ${H}`,
        backgroundColor: resolveColor(anim.doc.canvas.background),
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {GLOW_LEVELS.map((level, i) => (
            <filter
              key={level}
              id={`${glowId}-${i + 1}`}
              filterUnits="userSpaceOnUse"
              x={region.x}
              y={region.y}
              width={region.width}
              height={region.height}
              colorInterpolationFilters="sRGB"
            >
              <feMorphology in="SourceAlpha" operator="dilate" radius={1.5} result="wide" />
              <feGaussianBlur in="wide" stdDeviation={4} result="soft" />
              <feFlood floodColor={resolveColor('gold')} floodOpacity={level} />
              <feComposite in2="soft" operator="in" result="halo" />
              <feMerge>
                <feMergeNode in="halo" />
                <feMergeNode in="halo" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>
        {roots.map(renderSvg)}
      </svg>
      <div
        className="absolute left-0 top-0 font-sans"
        style={
          {
            width: W,
            height: H,
            transform: `scale(${k})`,
            transformOrigin: '0 0',
            '--jr-k': k,
          } as React.CSSProperties
        }
      >
        <div aria-hidden="true" className="absolute inset-0">
          {roots.map(renderHtml)}
        </div>
        {overlay}
      </div>
    </div>
  );
});
